import { Compass, Hammer } from 'lucide-react';
import { Link, Navigate, isRouteErrorResponse, useRouteError } from 'react-router';
import { Button, EmptyState, ErrorState, PageHeader } from '@/components/ui';
import { useAuth } from '@/lib/auth/AuthProvider';
import { AUDIENCE_HOME } from '@/lib/auth/roles';

/** `/` sends you to your own app, or to sign in. */
export function RootRedirect() {
  const { session } = useAuth();
  return <Navigate to={session ? AUDIENCE_HOME[session.audience] : '/login'} replace />;
}

export function NotFoundPage() {
  const { session } = useAuth();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-6">
      <EmptyState
        icon={Compass}
        title="Page not found"
        description="That address doesn’t exist, or it moved."
        action={
          <Link to={session ? AUDIENCE_HOME[session.audience] : '/login'}>
            <Button>{session ? 'Go to your dashboard' : 'Go to sign in'}</Button>
          </Link>
        }
      />
    </div>
  );
}

/** Anything that throws while rendering or loading a route lands here, inside the shell. */
export function RouteError() {
  const error = useRouteError();
  const message = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : error instanceof Error ? error.message : undefined;
  return (
    <div className="mx-auto max-w-lg py-16">
      <ErrorState error={new Error(message ?? 'An unexpected error occurred.')} title="This page hit a problem" onRetry={() => window.location.reload()} />
    </div>
  );
}

/** Honest stand-in for a section that is not built yet. It never pretends to have data. */
export function ComingSoon({ title }: { title: string }) {
  return (
    <>
      <PageHeader title={title} />
      <div className="rounded-card border border-dashed border-slate-300 bg-white">
        <EmptyState icon={Hammer} title={`${title} is on its way`} description="This section isn’t available yet. Nothing here is placeholder data." />
      </div>
    </>
  );
}
